/**
 * Commissions — agent commission tracking and rule setup.
 * Route: /commissions
 */
import { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  AppBadge, AppButton, AppCard, AppField, AppInput, AppSelect, ConfirmAction,
} from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { IconCheck, IconSpinner } from "../../../shared/components/icons";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { api } from "../../../shared/api/client";
import { getUser, hasPermission } from "../../auth/session";

type CommissionStatus = "pending" | "approved" | "paid" | "rejected";

type Commission = {
  id: number;
  agent_id: number;
  customer_id: number | null;
  commission_type: string;
  amount_tzs: number;
  status: CommissionStatus;
  approved_by: number | null;
  approved_at: string | null;
  paid_at: string | null;
  notes: string;
  created_at: string;
};

type CommissionRule = {
  id: number;
  name: string;
  rule_type: string;
  amount_tzs: number;
  device_type_id: number | null;
  schedule: string;
  is_active: boolean;
  created_at: string | null;
};

const RULE_TYPES = [
  { value: "registration", label: "Per registration" },
  { value: "first_payment", label: "First payment" },
  { value: "milestone", label: "Milestone" },
  { value: "referral", label: "Referral" },
];

function statusBadge(status: CommissionStatus) {
  const map: Record<CommissionStatus, { tone: "success" | "warning" | "error" | "info" | "neutral"; label: string }> = {
    pending: { tone: "warning", label: "Pending" },
    approved: { tone: "info", label: "Approved" },
    paid: { tone: "success", label: "Paid" },
    rejected: { tone: "error", label: "Rejected" },
  };
  const { tone, label } = map[status];
  return <AppBadge tone={tone}>{label}</AppBadge>;
}

function CreateRuleForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState("");
  const [ruleType, setRuleType] = useState("registration");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !amount) {
      setErr("Name and amount are required");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post("/commission-rules", {
        name: name.trim(),
        rule_type: ruleType,
        amount_tzs: Number(amount),
      });
      setName(""); setAmount(""); setRuleType("registration");
      onCreated();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : "Failed to create rule");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="pg-two-col-form">
      <AppField label="Rule name">
        <AppInput
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Sales rep registration bonus"
          disabled={busy}
        />
      </AppField>
      <AppField label="Trigger">
        <AppSelect value={ruleType} onChange={(e) => setRuleType(e.target.value)} disabled={busy}>
          {RULE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </AppSelect>
      </AppField>
      <AppField label="Amount (TZS)" error={err || undefined}>
        <AppInput type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="5000" disabled={busy} />
      </AppField>
      <div style={{ alignSelf: "end" }}>
        <AppButton type="submit" disabled={busy}>
          {busy ? <><IconSpinner size={14} /> Creating…</> : "Add Rule"}
        </AppButton>
      </div>
    </form>
  );
}

export function Commissions() {
  const user = getUser();
  const canApprove = hasPermission(user, "commission.approve") || user?.role === "business_admin";
  const canPay = hasPermission(user, "commission.pay") || user?.role === "business_admin";

  const [tab, setTab] = useState<"commissions" | "rules">("commissions");
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [rules, setRules] = useState<CommissionRule[]>([]);
  const [fetchStatus, setFetchStatus] = useState<"loading" | "error" | "success">("loading");
  const [pageError, setPageError] = useState("");
  const [actionBusy, setActionBusy] = useState<number | null>(null);
  const [confirmPay, setConfirmPay] = useState<Commission | null>(null);

  const load = async () => {
    setFetchStatus("loading");
    try {
      const [cd, rd] = await Promise.all([
        api.get<{ commissions: Commission[] }>("/commissions"),
        api.get<{ rules: CommissionRule[] }>("/commission-rules"),
      ]);
      setCommissions(cd.commissions ?? []);
      setRules(rd.rules ?? []);
      setFetchStatus("success");
    } catch (ex: unknown) {
      setPageError(ex instanceof Error ? ex.message : "Failed to load commissions");
      setFetchStatus("error");
    }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id: number) => {
    setActionBusy(id);
    try { await api.post(`/commissions/${id}/approve`, {}); await load(); }
    catch { /* row stays actionable */ }
    finally { setActionBusy(null); }
  };

  const pay = async (id: number) => {
    setActionBusy(id);
    try { await api.post(`/commissions/${id}/pay`, {}); await load(); }
    catch { /* row stays actionable */ }
    finally { setActionBusy(null); }
  };

  const columns: ColumnDef<Commission, unknown>[] = [
    { id: "agent", header: "Agent", accessorFn: (r) => `Agent #${r.agent_id}` },
    { id: "type", header: "Type", accessorFn: (r) => r.commission_type.replace(/_/g, " ") },
    { id: "amount", header: "Amount", accessorFn: (r) => `TZS ${r.amount_tzs.toLocaleString("en-US")}` },
    { id: "status", header: "Status", cell: ({ row }) => statusBadge(row.original.status) },
    { id: "created_at", header: "Earned", accessorFn: (r) => new Date(r.created_at).toLocaleDateString("en-GB") },
    { id: "paid_at", header: "Paid", accessorFn: (r) => (r.paid_at ? new Date(r.paid_at).toLocaleDateString("en-GB") : "—") },
    {
      id: "actions", header: "", enableSorting: false,
      cell: ({ row }) => {
        const c = row.original;
        if (c.status === "pending" && canApprove) {
          return (
            <AppButton type="button" disabled={actionBusy === c.id} onClick={(e) => { e.stopPropagation(); approve(c.id); }}>
              {actionBusy === c.id ? <IconSpinner size={12} /> : "Approve"}
            </AppButton>
          );
        }
        if (c.status === "approved" && canPay) {
          return (
            <AppButton type="button" variant="secondary" disabled={actionBusy === c.id} onClick={(e) => { e.stopPropagation(); setConfirmPay(c); }}>
              {actionBusy === c.id ? <IconSpinner size={12} /> : "Mark Paid"}
            </AppButton>
          );
        }
        return null;
      },
    },
  ];

  const ruleColumns: ColumnDef<CommissionRule, unknown>[] = [
    { accessorKey: "name", header: "Rule" },
    { id: "rule_type", header: "Trigger", accessorFn: (r) => r.rule_type.replace(/_/g, " ") },
    { id: "amount_tzs", header: "Amount", accessorFn: (r) => `TZS ${r.amount_tzs.toLocaleString("en-US")}` },
    {
      id: "is_active", header: "Status",
      cell: ({ row }) => row.original.is_active
        ? <AppBadge tone="success"><IconCheck size={12} /> Active</AppBadge>
        : <AppBadge tone="neutral">Inactive</AppBadge>,
    },
  ];

  if (fetchStatus === "loading" || fetchStatus === "error") {
    return (
      <div>
        <PageHeader title="Commissions" subtitle="Agent earnings from registration, first payment, milestone, or referral rules." />
        <PageState
          status={fetchStatus}
          error={pageError}
          onRetry={load}
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Commissions"
        subtitle="Agent earnings from registration, first payment, milestone, or referral rules. Create a rule before expecting rows here."
      />

      <div className="pg-tabs">
        <button type="button" className={`pg-tab${tab === "commissions" ? " active" : ""}`} onClick={() => setTab("commissions")}>
          Commissions ({commissions.length})
        </button>
        <button type="button" className={`pg-tab${tab === "rules" ? " active" : ""}`} onClick={() => setTab("rules")}>
          Commission Rules ({rules.length})
        </button>
      </div>

      {tab === "commissions" && (
        <PageState
          status={commissions.length === 0 ? "empty" : "success"}
          emptyTitle="No commissions yet"
          emptyHint={
            rules.length === 0
              ? "No rules configured. Open the Commission Rules tab and add triggers: Per registration, First payment, Milestone, or Referral."
              : "Rules exist, but no qualifying events have earned commissions yet (registration / first payment / milestone / referral)."
          }
        >
          <AppCard>
            <AppDataTable data={commissions} columns={columns} />
          </AppCard>
        </PageState>
      )}

      {tab === "rules" && (
        <>
          {canApprove && (
            <AppCard title="Add a commission rule">
              <p className="pg-text-muted" style={{ marginTop: 0 }}>
                Triggers: <strong>Per registration</strong>, <strong>First payment</strong>,{" "}
                <strong>Milestone</strong>, <strong>Referral</strong>. Typical earners: sales reps and
                technicians. Business/commercial configure and approve; customers do not earn agent commission.
              </p>
              <CreateRuleForm onCreated={load} />
            </AppCard>
          )}
          <AppCard>
            {rules.length === 0
              ? <p className="pg-text-muted">No commission rules yet — add one above so agent events can create earnings.</p>
              : <AppDataTable data={rules} columns={ruleColumns} />}
          </AppCard>
        </>
      )}

      <ConfirmAction
        open={!!confirmPay}
        title="Mark commission paid?"
        message={
          confirmPay
            ? `Mark TZS ${confirmPay.amount_tzs.toLocaleString("en-US")} for agent #${confirmPay.agent_id} as paid?`
            : ""
        }
        confirmLabel="Mark paid"
        danger
        busy={actionBusy === confirmPay?.id}
        onCancel={() => setConfirmPay(null)}
        onConfirm={() => {
          const c = confirmPay;
          setConfirmPay(null);
          if (c) void pay(c.id);
        }}
      />
    </div>
  );
}
